
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Instagram, Linkedin, Twitter, Facebook, ArrowRight, User, ShieldCheck, Map, LogOut, LayoutDashboard, Zap } from 'lucide-react';
import { PrimaryButton } from './UI';
import { AuthModal } from './AuthModals';
import { NavItem } from '../types';
import { storage } from '../utils/storage';
import { CookieBanner } from './CookieBanner';
import { useAuth } from '../utils/useAuth';
import { db } from '../utils/firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
const NAVIGATION: NavItem[] = [
  { title: "Home", href: "/" },
  { title: "Services", href: "/services" },
  { title: "Portfolio", href: "/portfolio" },
  { title: "Booking", href: "/booking" },
  { title: "Blog", href: "/blog" },
  { title: "About", href: "/about" },
  { title: "Contact", href: "/contact" },
];

export const Header = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteName, setSiteName] = useState('Nescotech');
  const { user, isAuthenticated, logout } = useAuth();
  const isAdmin = user?.email?.toLowerCase().trim() === 'hamzayasin12443@gmail.com';

  // Auth Modal State
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check initial auth & settings
    const init = () => {
      const settings = storage.getSettings();
      setSiteName(settings.general.siteName || 'Nescotech');

      // APPLY THEME REAL-TIME
      const root = document.documentElement;
      if (settings.theme) {
        root.style.setProperty('--brand-primary', settings.theme.primaryColor || '#84cc16');
        root.style.setProperty('--brand-secondary', settings.theme.secondaryColor || '#eab308');
      }
    };
    init();

    // Listen for global updates
    window.addEventListener('auth-change', init);
    window.addEventListener('data-change', init);

    // Global listener for auth modal
    const handleOpenAuth = (e: any) => {
      if (e.detail?.mode) setAuthMode(e.detail.mode);
    };
    window.addEventListener('nescotech-open-auth', handleOpenAuth);

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Calculate progress over the first 100px of scroll
          const progress = Math.min(Math.max(window.scrollY / 100, 0), 1);
          setScrollProgress(progress);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Init
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('auth-change', init);
      window.removeEventListener('data-change', init);
      window.removeEventListener('nescotech-open-auth', handleOpenAuth);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const isScrolled = scrollProgress > 0.1;

  // Dynamic Liquid Glass Styles
  const glassStyle = {
    backgroundColor: `rgba(255, 255, 255, ${0.05 + (scrollProgress * 0.35)})`, // Interpolate opacity
    backdropFilter: `blur(${scrollProgress * 24}px) saturate(${100 + (scrollProgress * 80)}%)`, // Interpolate blur & saturation
    borderColor: `rgba(255, 255, 255, ${scrollProgress * 0.4})`, // Fade in border
    boxShadow: `0 8px 32px 0 rgba(31, 38, 135, ${scrollProgress * 0.1})`, // Fade in shadow
  };

  return (
    <>
      <header className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out
        ${isScrolled ? 'py-4' : 'py-6'}
      `}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div
            className="relative rounded-full flex items-center justify-between px-6 py-3 overflow-hidden border border-transparent transition-all duration-300"
            style={glassStyle}
          >

            {/* Shimmer Effect on Scroll */}
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-700 [backface-visibility:hidden] [transform:translateZ(0)]"
              style={{ opacity: scrollProgress }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            </div>

            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer group relative z-10" onClick={() => navigate('/')}>
              <img
                src="/logo.png"
                alt={`${siteName} Logo`}
                fetchPriority="high"
                width="120"
                height="40"
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8 relative z-10">
              {NAVIGATION.map((item) => (
                <NavLink
                  key={item.title}
                  to={item.href}
                  onMouseEnter={() => {
                    // Optimized Prefetch: Trigger lazy load on hover
                    if (item.href === '/services') import('../pages/Services');
                    if (item.href === '/portfolio') import('../pages/Portfolio');
                    if (item.href === '/booking') import('../pages/Booking');
                    if (item.href === '/blog') import('../pages/Blog');
                  }}
                  className={({ isActive }) => `
                    text-sm font-medium transition-all duration-300 hover:text-brand-lime relative group
                    ${isActive ? 'text-brand-lime' : 'text-gray-600'}
                  `}
                >
                  {item.title}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-lime transition-all duration-300 group-hover:w-full opacity-0 group-hover:opacity-100" />
                </NavLink>
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-4 relative z-10">
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                    className="font-bold text-sm text-gray-600 hover:text-brand-lime transition-colors flex items-center gap-2"
                  >
                    <LayoutDashboard size={16} /> {isAdmin ? 'Admin Panel' : 'Dashboard'}
                  </button>
                  <button
                    onClick={async () => { await logout(); navigate('/'); }}
                    className="font-bold text-sm text-gray-600 hover:text-red-500 transition-colors flex items-center gap-2"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-lime to-brand-yellow flex items-center justify-center text-white font-bold text-sm shadow-md">
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      user?.displayName?.charAt(0) || user?.email?.charAt(0).toUpperCase() || 'U'
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setAuthMode('login')}
                    className="font-bold text-sm text-gray-700 hover:text-brand-lime px-4 py-2 rounded-full hover:bg-white/50 transition-all duration-300"
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => setAuthMode('signup')}
                    className="font-bold text-sm text-white bg-gradient-to-r from-brand-lime to-brand-yellow px-6 py-2.5 rounded-full hover:shadow-lg hover:shadow-brand-lime/30 hover:scale-105 transition-all duration-300 border border-white/20"
                  >
                    Get Started
                  </button>
                </>
              )}


            </div>

            {/* Mobile Toggle */}
            <button
              className="md:hidden text-gray-700 relative z-10 p-2"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open Mobile Menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Auth Modal Popup */}
      <AuthModal
        mode={authMode}
        onClose={() => setAuthMode(null)}
        onSwitch={() => setAuthMode(prev => prev === 'login' ? 'signup' : 'login')}
        onLoginSuccess={(email) => {
          setAuthMode(null);
          // Auto-redirect admin to admin dashboard, users to user dashboard
          if (email?.toLowerCase() === 'hamzayasin12443@gmail.com') {
            navigate('/admin');
          } else {
            navigate('/dashboard');
          }
        }}
      />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-white/80 backdrop-blur-2xl p-6 flex flex-col animate-fade-in-up">
          <div className="flex justify-between items-center mb-10">
            <div className="flex items-center gap-3" onClick={() => { setMobileMenuOpen(false); navigate('/'); }}>
              <img
                src="/logo.png"
                alt={`${siteName} Logo`}
                className="h-10 w-auto object-contain"
              />
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close Mobile Menu"
              className="p-2 bg-gray-100/50 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X />
            </button>
          </div>
          <nav className="flex flex-col gap-6 text-2xl font-medium">
            {NAVIGATION.map((item) => (
              <NavLink
                key={item.title}
                to={item.href}
                className={({ isActive }) => isActive ? 'text-brand-lime translate-x-2 transition-transform' : 'text-gray-800 hover:translate-x-2 transition-transform'}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.title}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto space-y-4">
            {isAuthenticated ? (
              <div className="space-y-4">
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate(isAdmin ? '/admin' : '/dashboard'); }}
                  className="w-full py-3 rounded-xl bg-brand-lime text-gray-900 font-bold flex items-center justify-center gap-2"
                >
                  <LayoutDashboard size={18} /> {isAdmin ? 'Admin Panel' : 'Dashboard'}
                </button>
                <button
                  onClick={async () => { setMobileMenuOpen(false); await logout(); navigate('/'); }}
                  className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-bold flex items-center justify-center gap-2"
                >
                  <LogOut size={18} /> Logout
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => { setMobileMenuOpen(false); setAuthMode('login'); }}
                  className="py-3 rounded-xl border border-gray-200 font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); setAuthMode('signup'); }}
                  className="py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-black transition-colors"
                >
                  Sign Up
                </button>
              </div>
            )}

            <div className="p-4 rounded-2xl bg-white/50 border border-white/60">
              <p className="text-sm text-gray-500 mb-2">Need immediate assistance?</p>
              <p className="text-lg font-bold text-gray-900">+92 312 1528938</p>
            </div>
            <PrimaryButton fullWidth onClick={() => { navigate('/booking'); setMobileMenuOpen(false); }}>Start Project</PrimaryButton>
          </div>
        </div>
      )}
    </>
  );
};

export const Footer = () => {
  const [scrollRatio, setScrollRatio] = useState(0);
  const [settings, setSettings] = useState(storage.getSettings());
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Keep settings in sync
    const handleUpdate = () => setSettings(storage.getSettings());
    window.addEventListener('data-change', handleUpdate);

    const handleScroll = () => {
      // Calculate scroll progress (0 to 1) based on total scrollable height
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.min(1, Math.max(0, scrollTop / docHeight));
      setScrollRatio(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initialize on mount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('data-change', handleUpdate);
    };
  }, []);

  // Dynamic Styles based on scroll
  const dynamicGlassStyle = {
    backgroundColor: `rgba(255, 255, 255, ${0.2 + (scrollRatio * 0.2)})`, // 0.2 -> 0.4
    backdropFilter: `blur(${20 + (scrollRatio * 12)}px) saturate(${150 + (scrollRatio * 30)}%)`, // 20px->32px blur
  };

  return (
    <footer className="relative mt-32 pt-20 pb-10 overflow-hidden transition-all duration-700 z-0">

      {/* 1. Dynamic Liquid Glass Background Layer */}
      <div
        className="absolute inset-0 transition-all duration-300 ease-out border-t border-white/30"
        style={dynamicGlassStyle}
      />

      {/* 2. Refractive Top Border Effect */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/80 to-transparent shadow-[0_0_15px_rgba(255,255,255,0.8)] z-20" />

      {/* 3. Subtle Liquid Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo.png" alt="Logo" className="h-12 w-auto object-contain" />
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-6 font-medium">
              {settings.general.tagline}
            </p>
            <div className="flex gap-4">
              {[
                { Icon: Instagram, link: settings.socials?.instagram, label: 'Instagram' },
                { Icon: Twitter, link: settings.socials?.twitter, label: 'Twitter' },
                { Icon: Linkedin, link: settings.socials?.linkedin, label: 'LinkedIn' },
                { Icon: Facebook, link: settings.socials?.facebook, label: 'Facebook' }
              ].map(({ Icon, link, label }, i) => (
                <a
                  key={i}
                  href={link || "#"}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Follow us on ${label}`}
                  className="w-10 h-10 rounded-full bg-white/60 border border-white/50 flex items-center justify-center text-gray-500 hover:bg-brand-lime hover:text-white hover:border-brand-lime transition-all transform hover:scale-110 shadow-sm backdrop-blur-sm"
                >
                  <Icon size={18} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-6">Services</h3>
            <ul className="space-y-4 text-sm text-gray-600 font-medium">
              <li><button onClick={() => navigate('/services')} className="hover:text-brand-lime transition-colors">Web Development</button></li>
              <li><button onClick={() => navigate('/services')} className="hover:text-brand-lime transition-colors">Mobile Apps</button></li>
              <li><button onClick={() => navigate('/services')} className="hover:text-brand-lime transition-colors">AI & Automation</button></li>
              <li><button onClick={() => navigate('/services')} className="hover:text-brand-lime transition-colors">Digital Marketing</button></li>
              <li><button onClick={() => navigate('/testimonials')} className="hover:text-brand-lime transition-colors">Testimonials</button></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-6">Legal & Compliance</h3>
            <ul className="space-y-4 text-sm text-gray-600 font-medium">
              <li><button onClick={() => navigate('/legal/privacy-policy')} className="hover:text-brand-lime transition-colors">Privacy Policy</button></li>
              <li><button onClick={() => navigate('/legal/terms-of-service')} className="hover:text-brand-lime transition-colors">Terms of Service</button></li>
              <li><button onClick={() => navigate('/legal/cookie-policy')} className="hover:text-brand-lime transition-colors">Cookie Policy</button></li>
              <li><button onClick={() => navigate('/legal/accessibility')} className="hover:text-brand-lime transition-colors">Accessibility (ADA)</button></li>
              <li><button onClick={() => navigate('/complaint-center')} className="hover:text-brand-lime transition-colors">Complaint Center</button></li>
              <li><button onClick={() => navigate('/legal/data-request')} className="hover:text-brand-lime transition-colors text-brand-dark flex items-center gap-1"><ShieldCheck size={12} /> Data Request</button></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-gray-900 mb-6">Newsletter</h3>
            <p className="text-sm text-gray-600 mb-4">Subscribe for the latest trends and tech news.</p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Successfully subscribed!
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (newsletterEmail && newsletterEmail.includes('@')) {
                  try {
                    // Check if already subscribed in Firebase
                    const existingQuery = query(
                      collection(db, 'newsletter'),
                      where('email', '==', newsletterEmail)
                    );
                    const existing = await getDocs(existingQuery);

                    if (!existing.empty) {
                      alert('This email is already subscribed!');
                      return;
                    }

                    // Save to Firebase
                    await addDoc(collection(db, 'newsletter'), {
                      email: newsletterEmail,
                      subscribedAt: new Date().toISOString(),
                      source: 'footer',
                      status: 'active'
                    });
                    console.log('✅ Newsletter subscriber saved to Firebase');

                    // Also save to localStorage as backup
                    storage.addNewsletterSubscriber({
                      id: Date.now().toString(),
                      email: newsletterEmail,
                      subscribedAt: new Date().toISOString(),
                      source: 'footer',
                      status: 'active'
                    });

                    setSubscribed(true);
                    setNewsletterEmail('');
                  } catch (error) {
                    console.error('❌ Newsletter subscription error:', error);
                    alert('Subscription failed. Please try again.');
                  }
                }
              }} className="flex gap-2">
                <input
                  type="email"
                  placeholder="Email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="flex-1 px-4 py-2 rounded-lg bg-white/60 border border-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50 transition-all"
                />
                <button
                  type="submit"
                  aria-label="Subscribe to newsletter"
                  className="px-4 py-2 rounded-lg bg-brand-lime text-white font-bold hover:bg-brand-yellow transition-colors shadow-lg shadow-brand-lime/20 flex items-center justify-center"
                >
                  <ArrowRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="pt-8 border-t border-gray-200/50 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium text-gray-500">
          <p>&copy; 2025 {settings.general.siteName}. All rights reserved.</p>
          <div className="flex gap-6">
            <button onClick={() => navigate('/sitemap')} className="hover:text-gray-900 flex items-center gap-1"><Map size={12} /> Sitemap</button>
            <button onClick={() => navigate('/legal/refund-policy')} className="hover:text-gray-900">Refund Policy</button>
            <button onClick={() => navigate('/legal/acceptable-use')} className="hover:text-gray-900">Acceptable Use</button>
            <button onClick={() => navigate('/legal/disclaimer')} className="hover:text-gray-900">Disclaimer</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }: { children?: React.ReactNode }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen relative font-sans text-gray-800 selection:bg-brand-lime/30 selection:text-brand-dark">
      {/* Global Background Blobs */}
      <div className="fixed inset-0 -z-50 overflow-hidden pointer-events-none bg-[#f8fafc]">
        <div className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] bg-brand-lime/5 rounded-full mix-blend-multiply filter blur-[120px] animate-blob" />
        <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vw] bg-brand-yellow/5 rounded-full mix-blend-multiply filter blur-[120px] animate-blob animation-delay-2000" />
        <div className="absolute bottom-[-20%] left-[20%] w-[50vw] h-[50vw] bg-blue-100/20 rounded-full mix-blend-multiply filter blur-[120px] animate-blob animation-delay-4000" />
        {/* Grain Overlay - Local SVG Data URI to fix 404 and reduce network requests */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }}
        />
      </div>

      <Header />
      <main className="relative min-h-screen">
        {children}
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
};
